import React, { useState, useEffect } from 'react';
import { X, Calendar, Save, Users, TrendingUp, Check, XIcon } from 'lucide-react';
import { Modal, Button, Input, LoadingSpinner } from '../ui';
import { toast } from '../ui/Toaster';
import { adminEventService, type AdminEventDetail } from '../../services/adminEventService';
import { getImageUrl } from '../../utils/imageUtils';

interface EventEditData {
  title: string;
  description: string;
  slug: string;
  location: string;
  startDate: string;
  endDate: string;
  image: string;
}

interface EventDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  onEventUpdated?: () => void;
}

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  isOpen,
  onClose,
  eventId,
  onEventUpdated
}) => {
  const [eventData, setEventData] = useState<AdminEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'info' | 'attendees' | 'groups'>('edit');
  const [adminNote, setAdminNote] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editForm, setEditForm] = useState<EventEditData>({
    title: '',
    description: '',
    slug: '',
    location: '',
    startDate: '',
    endDate: '',
    image: ''
  });
  const [newImage, setNewImage] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  useEffect(() => {
    if (isOpen && eventId) {
      loadEventData();
    }
  }, [isOpen, eventId]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      const event = await adminEventService.getEventDetail(parseInt(eventId));
      
      const formData: EventEditData = {
        title: event.title || '',
        description: event.description || '',
        slug: event.slug || '',
        location: event.location || '',
        startDate: event.startDate ? new Date(event.startDate).toISOString().slice(0, 10) : '',
        endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 10) : '',
        image: event.image || ''
      };
      
      setEventData(event);
      setEditForm(formData);
    } catch (error: any) {
      console.error('Failed to load event:', error);
      toast.error('Fout', 'Kon evenement niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Determine if we need to use FormData (for file uploads)
      const hasFileUpload = newImage || removeImage;
      
      
      if (hasFileUpload) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('title', editForm.title.trim());
        formData.append('description', editForm.description.trim());
        formData.append('slug', editForm.slug.trim());
        formData.append('location', editForm.location.trim());
        
        if (editForm.startDate) {
          formData.append('startDate', new Date(`${editForm.startDate}T12:00:00`).toISOString());
        }
        if (editForm.endDate) {
          formData.append('endDate', new Date(`${editForm.endDate}T23:59:59`).toISOString());
        }
        
        // Handle image changes
        if (removeImage) {
          formData.append('removeImage', 'true');
        } else if (newImage) {
          formData.append('eventImage', newImage);
        }
        
        await adminEventService.updateEventWithFile(parseInt(eventId), formData);
      } else {
        // Use regular JSON for text-only updates
        const updateData = {
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          slug: editForm.slug.trim(),
          location: editForm.location.trim(),
          startDate: editForm.startDate ? new Date(`${editForm.startDate}T12:00:00`).toISOString() : null,
          endDate: editForm.endDate ? new Date(`${editForm.endDate}T23:59:59`).toISOString() : null,
          image: editForm.image.trim() || null
        };
        
        await adminEventService.updateEvent(parseInt(eventId), updateData);
      }
      
      toast.success('Succes', 'Evenement bijgewerkt');
      
      // Reset image state
      setNewImage(null);
      setRemoveImage(false);
      
      // Clear file inputs
      if ((window as any).replaceImageInput) {
        (window as any).replaceImageInput.value = '';
      }
      if ((window as any).addImageInput) {
        (window as any).addImageInput.value = '';
      }
      if ((window as any).newImageInput) {
        (window as any).newImageInput.value = '';
      }
      
      onEventUpdated?.();
      onClose();
      
    } catch (error: any) {
      console.error('Failed to update event:', error);
      toast.error('Fout', error.message || 'Kon evenement niet bijwerken');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof EventEditData, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApproveEvent = async () => {
    if (!eventData) return;

    try {
      setApproving(true);
      // Use bulk approve with single event for consistency
      await adminEventService.bulkApproveEvents([eventData.id]);
      toast.success('Succes', 'Evenement gemarkeerd als gecontroleerd');
      onEventUpdated?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to approve event:', error);
      toast.error('Fout', error.message || 'Kon evenement niet markeren als gecontroleerd');
    } finally {
      setApproving(false);
    }
  };

  const handleRejectEvent = async () => {
    if (!eventData) return;

    try {
      setRejecting(true);
      // Use bulk reject with single event and reason
      await adminEventService.bulkRejectEvents([eventData.id], adminNote.trim() || undefined);
      toast.success('Succes', 'Evenement gedepubliceerd');
      onEventUpdated?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to reject event:', error);
      toast.error('Fout', error.message || 'Kon evenement niet depubliceren');
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING: 'sf-badge-cyan',
      PUBLISHED: 'sf-badge-lime',
      REJECTED: 'sf-badge-pink',
      CANCELLED: 'sf-badge-pink'
    };

    return (
      <span className={statusColors[status as keyof typeof statusColors] || 'inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body'}>
        {status}
      </span>
    );
  };

  // "Te controleren": nog niet gecontroleerd en niet al gedepubliceerd
  const needsReview = Boolean(eventData && !eventData.reviewedAt && eventData.status !== 'REJECTED');

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="flex items-center justify-between p-6 border-b border-white/[.08]">
        <div className="flex items-center space-x-3">
          <Calendar className="h-6 w-6 text-neon-cyan" />
          <div>
            <h2 className="text-xl font-bold text-fg-bright">
              {eventData?.title || 'Evenement details'}
            </h2>
            {eventData && (
              <div className="flex items-center space-x-2 mt-1">
                {getStatusBadge(eventData.status)}
                <span className="text-sm text-fg-meta">•</span>
                <span className="font-mono text-[11px] uppercase tracking-[.16em] text-fg-meta">{eventData.type}</span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/[.06] rounded-full transition-colors"
        >
          <X className="h-5 w-5 text-fg-muted" />
        </button>
      </div>

      {/* Tabs */}
      {!loading && eventData && (
        <div className="border-b border-white/[.08]">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'edit', label: 'Bewerken', icon: Calendar },
              { key: 'info', label: 'Evenement informatie', icon: TrendingUp },
              { key: 'attendees', label: 'Deelnemers', icon: Users },
              { key: 'groups', label: 'Groepen', icon: Users }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                  activeTab === key
                    ? 'border-neon-cyan text-neon-cyan'
                    : 'border-transparent text-fg-muted hover:text-fg-soft'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : eventData ? (
          <>
            {/* Edit Tab */}
            {activeTab === 'edit' && (
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Titel *
                  </label>
                  <Input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Evenement titel"
                    required
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Slug *
                  </label>
                  <Input
                    type="text"
                    value={editForm.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="evenement-slug"
                    required
                  />
                  <p className="text-xs text-fg-muted mt-1">
                    URL-vriendelijke versie van de titel (geen spaties, kleine letters)
                  </p>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Locatie *
                  </label>
                  <Input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Stad, Land"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Beschrijving
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Evenement beschrijving"
                    rows={4}
                    className="sf-input"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Startdatum *
                  </label>
                  <Input
                    type="date"
                    value={editForm.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    required
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Einddatum
                  </label>
                  <Input
                    type="date"
                    value={editForm.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                  />
                </div>

                {/* Featured Image */}
                <div>
                  <label className="block text-sm font-semibold text-fg-soft mb-2">
                    Uitgelichte afbeelding
                  </label>
                  
                  {/* Current Image Display */}
                  {(editForm.image || newImage) && !removeImage && (
                    <div className="sf-item mb-4 p-4">
                      <p className="text-sm text-fg-muted mb-2">
                        {newImage ? 'Nieuwe afbeelding:' : 'Huidige afbeelding:'}
                      </p>
                      <div className="flex items-center space-x-4">
                        {newImage ? (
                          <img 
                            src={URL.createObjectURL(newImage)}
                            alt="New event image" 
                            className="w-32 h-16 object-cover rounded-lg border border-white/10"
                          />
                        ) : (
                          <img 
                            src={getImageUrl(editForm.image) || ''}
                            alt="Current event image" 
                            className="w-32 h-16 object-cover rounded-lg border border-white/10"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex flex-col space-y-2">
                          <input
                            ref={(input) => {
                              if (input) {
                                (window as any).replaceImageInput = input;
                              }
                            }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                if (file.size > 5 * 1024 * 1024) {
                                  toast.error('Fout', 'Afbeelding moet kleiner zijn dan 5MB');
                                  return;
                                }
                                setNewImage(file);
                                setRemoveImage(false);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-neon-cyan"
                            onClick={() => {
                              const input = (window as any).replaceImageInput;
                              if (input) {
                                input.click();
                              }
                            }}
                          >
                            Vervang afbeelding
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setRemoveImage(true);
                              setNewImage(null);
                            }}
                            className="text-neon-pink-soft"
                          >
                            Verwijder afbeelding
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* No Image State */}
                  {!editForm.image && !newImage && !removeImage && (
                    <div className="sf-dropzone mb-4 p-4 text-center">
                      <p className="text-sm text-fg-muted mb-2">Geen afbeelding</p>
                      <input
                        ref={(input) => {
                          if (input) {
                            (window as any).addImageInput = input;
                          }
                        }}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('Fout', 'Afbeelding moet kleiner zijn dan 5MB');
                              return;
                            }
                            setNewImage(file);
                            setRemoveImage(false);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-neon-cyan"
                        onClick={() => {
                          const input = (window as any).addImageInput;
                          if (input) {
                            input.click();
                          }
                        }}
                      >
                        Afbeelding toevoegen
                      </Button>
                    </div>
                  )}
                  
                  {/* Image Removed State */}
                  {removeImage && (
                    <div className="mb-4 p-4 rounded-field border border-neon-pink/40 bg-neon-pink/[.08] text-center">
                      <p className="text-sm text-neon-pink-soft mb-2">Afbeelding wordt verwijderd bij opslaan</p>
                      <div className="flex justify-center space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setRemoveImage(false)}
                        >
                          Annuleren
                        </Button>
                        <input
                          ref={(input) => {
                            if (input) {
                              (window as any).newImageInput = input;
                            }
                          }}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (file.size > 5 * 1024 * 1024) {
                                toast.error('Fout', 'Afbeelding moet kleiner zijn dan 5MB');
                                return;
                              }
                              setNewImage(file);
                              setRemoveImage(false);
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-neon-cyan"
                          onClick={() => {
                            const input = (window as any).newImageInput;
                            if (input) {
                              input.click();
                            }
                          }}
                        >
                          Nieuwe afbeelding toevoegen
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-fg-muted mt-2">
                    Aanbevolen: 4:2 aspectverhouding, max 5MB
                  </p>
                </div>
              </div>
            )}

            {/* Event Information Tab (merged Statistics + Admin Actions) */}
            {activeTab === 'info' && (
              <div className="space-y-6">
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sf-card p-4">
                    <div className="flex items-center">
                      <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-gradient text-ink">
                        <Users className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-2xl font-bold text-fg-bright">
                          {eventData.groups?.reduce((total, group) => total + group.memberCount, 0) || 0}
                        </p>
                        <p className="sf-label-mono">Totaal deelnemers</p>
                      </div>
                    </div>
                  </div>
                  <div className="sf-card p-4">
                    <div className="flex items-center">
                      <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-avatar-violet text-ink">
                        <Users className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-2xl font-bold text-fg-bright">{eventData.statistics?.associatedGroups || eventData.groups?.length || 0}</p>
                        <p className="sf-label-mono">Groepen</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Current Status */}
                <div className="sf-item p-4">
                  <h4 className="font-semibold text-fg-bright mb-3">Huidige status</h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(eventData.status)}
                      <span className="text-sm text-fg-body">
                        {eventData.status === 'PENDING' && 'Wacht op publicatie'}
                        {eventData.status === 'PUBLISHED' && 'Live en zichtbaar'}
                        {eventData.status === 'REJECTED' && 'Gedepubliceerd (afgewezen)'}
                        {eventData.status === 'CANCELLED' && 'Geannuleerd'}
                      </span>
                      {!eventData.reviewedAt && eventData.status !== 'REJECTED' && (
                        <span className="sf-badge-cyan">Nog te controleren</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Admin Actions */}
                {needsReview && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-fg-bright">Admin acties</h4>

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-semibold text-fg-soft mb-2">
                        Reden voor depublicatie (optioneel)
                      </label>
                      <textarea
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value)}
                        placeholder="Voeg een reden toe voor de evenement maker..."
                        rows={3}
                        className="sf-input"
                      />
                      <p className="text-xs text-fg-muted mt-1">
                        Deze reden wordt getoond aan de evenement maker en per e-mail verstuurd wanneer je depubliceert.
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={handleApproveEvent}
                        disabled={approving || rejecting}
                        className="sf-btn-accept px-4 py-2.5 text-sm space-x-2"
                      >
                        {approving ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span>Markeren...</span>
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            <span>Markeer als gecontroleerd</span>
                          </>
                        )}
                      </button>

                      <button
                        onClick={handleRejectEvent}
                        disabled={approving || rejecting}
                        className="sf-btn-destructive px-4 py-2.5 text-sm space-x-2"
                      >
                        {rejecting ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span>Depubliceren...</span>
                          </>
                        ) : (
                          <>
                            <XIcon className="h-4 w-4" />
                            <span>Depubliceren</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Event Information */}
                <div className="sf-item p-4">
                  <h4 className="font-semibold text-fg-bright mb-3">Evenement informatie</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-fg-muted">Ingediend door:</span>
                      <span className="ml-2 text-fg">{eventData.creator?.username}</span>
                    </div>
                    <div>
                      <span className="text-fg-muted">E-mail maker:</span>
                      <span className="ml-2 text-fg">{eventData.creator?.email}</span>
                    </div>
                    <div>
                      <span className="text-fg-muted">Type:</span>
                      <span className="ml-2 text-fg">{eventData.type}</span>
                    </div>
                    {eventData.location && (
                      <div>
                        <span className="text-fg-muted">Locatie:</span>
                        <span className="ml-2 text-fg">{eventData.location}</span>
                      </div>
                    )}
                    {eventData.venue && (
                      <div>
                        <span className="text-fg-muted">Venue:</span>
                        <span className="ml-2 text-fg">{eventData.venue}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-fg-muted">Ingediend op:</span>
                      <span className="ml-2 text-fg">{formatDate(eventData.createdAt)}</span>
                    </div>
                    {eventData.updatedAt !== eventData.createdAt && (
                      <div>
                        <span className="text-fg-muted">Laatst bijgewerkt:</span>
                        <span className="ml-2 text-fg">{formatDate(eventData.updatedAt)}</span>
                      </div>
                    )}
                    {eventData.reviewedAt && (
                      <div>
                        <span className="text-fg-muted">Gecontroleerd op:</span>
                        <span className="ml-2 text-fg">{formatDate(eventData.reviewedAt)}</span>
                      </div>
                    )}
                    {eventData.rejectionReason && (
                      <div className="md:col-span-2">
                        <span className="text-fg-muted">Reden depublicatie:</span>
                        <span className="ml-2 text-fg">{eventData.rejectionReason}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Decision Helper for events awaiting review */}
                {needsReview && (
                  <div className="rounded-field border border-neon-cyan/25 bg-neon-cyan/[.07] p-4">
                    <h4 className="font-semibold text-neon-cyan mb-3">Beslissingshulp</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-fg-muted">Huidige deelnemers:</span>
                        <span className="ml-2 text-fg-bright font-semibold">
                          {eventData.groups?.reduce((total, group) => total + group.memberCount, 0) || 0} gebruikers
                        </span>
                      </div>
                      <div>
                        <span className="text-fg-muted">Groepen aangemaakt:</span>
                        <span className="ml-2 text-fg-bright font-semibold">
                          {eventData.statistics?.associatedGroups || eventData.groups?.length || 0} groepen
                        </span>
                      </div>
                      <div>
                        <span className="text-fg-muted">Event type:</span>
                        <span className="ml-2 text-fg-bright font-semibold">{eventData.type}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Attendees Tab - Users from Event Groups */}
            {activeTab === 'attendees' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-fg-bright">
                    Deelnemers ({eventData.groups?.reduce((total, group) => total + group.memberCount, 0) || 0})
                  </h4>
                  <span className="text-sm text-fg-muted">
                    Gebruikers in groepen van dit evenement
                  </span>
                </div>

                {eventData.groups && eventData.groups.length > 0 ? (
                  <div className="space-y-6">
                    {eventData.groups.map((group) => (
                      <div key={group.id} className="sf-card overflow-hidden">
                        <div className="bg-white/[.03] px-6 py-3 border-b border-white/[.08]">
                          <div className="flex justify-between items-center">
                            <h5 className="font-semibold text-fg-bright">{group.name}</h5>
                            <span className="font-mono text-[11px] uppercase tracking-[.16em] text-fg-meta">
                              {group.memberCount} lid{group.memberCount !== 1 ? 'en' : ''}
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="text-sm text-fg-body mb-4">
                            <p><strong className="text-fg-soft">Eigenaar:</strong> {group.owner.username}</p>
                            {group.description && <p><strong className="text-fg-soft">Beschrijving:</strong> {group.description}</p>}
                          </div>
                          <div className="text-sm text-fg-muted">
                            <p>
                              Deze groep heeft {group.memberCount} lid{group.memberCount !== 1 ? 'en' : ''}.
                              Voor gedetailleerde ledenlijst, gebruik de Groepen tab om naar de groeppagina te gaan.
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 italic text-fg-muted">
                    Geen groepen (en dus geen deelnemers) gevonden voor dit evenement
                  </div>
                )}
              </div>
            )}

            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-fg-bright">
                    Groepen ({eventData.groups?.length || 0})
                  </h4>
                </div>
                {eventData.groups && eventData.groups.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {eventData.groups.map((group) => (
                      <div key={group.id} className="sf-item p-4 transition-colors hover:bg-white/[.06]">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <a
                              href={`/groups/${group.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="sf-link hover:underline"
                            >
                              {group.name}
                            </a>
                            {group.description && (
                              <p className="text-sm text-fg-muted mt-1 line-clamp-2">
                                {group.description}
                              </p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-sm text-fg-muted">
                              <span className="flex items-center">
                                <Users className="h-4 w-4 mr-1 text-neon-cyan" />
                                {group.memberCount} leden
                              </span>
                              <span>Eigenaar: {group.owner.username}</span>
                            </div>
                            <p className="font-mono text-[11px] text-fg-meta mt-1">
                              Aangemaakt: {formatDate(group.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 italic text-fg-muted">
                    Geen groepen gevonden voor dit evenement
                  </div>
                )}
              </div>
            )}

          </>
        ) : (
          <div className="text-center py-12">
            <p className="italic text-fg-muted">Evenement niet gevonden</p>
          </div>
        )}
      </div>

      {/* Footer - Only show save button on edit tab */}
      {!loading && eventData && activeTab === 'edit' && (
        <div className="flex justify-end space-x-3 p-6 border-t border-white/[.08]">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Annuleren
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !editForm.title.trim() || !editForm.slug.trim() || !editForm.location.trim() || !editForm.startDate}
            className="flex items-center space-x-2"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Opslaan...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Opslaan</span>
              </>
            )}
          </Button>
        </div>
      )}
    </Modal>
  );
};